import { FetchArgs, createApi } from '@reduxjs/toolkit/query/react';
import { apiAuthenticatedQuery, internalHeaders } from './baseQuery';
import { extinctFalsyFromParams } from './nullExtict';
import {
  Company,
  CompanyFormData,
  CompanyType,
  getCompaniesCondition,
  getCompaniesResponse,
} from '../types/companies';
import { errorTagTypes, handleErrorTags } from './handleErrorTags';

export const companyApi = createApi({
  baseQuery: apiAuthenticatedQuery,
  reducerPath: 'company',
  tagTypes: ['Company', ...errorTagTypes],
  endpoints: (build) => {
    const getCompaniesQueryFn = (
      conditions: getCompaniesCondition
    ): Omit<FetchArgs, 'headers'> => ({
      url: `/companies`,
      params: extinctFalsyFromParams(conditions),
    });
    const getCompaniesQueryOptions: Parameters<
      typeof build.query<getCompaniesResponse, getCompaniesCondition>
    >[0] = {
      query: getCompaniesQueryFn,
      providesTags: (res, error) => [
        ...(res?.companies || []).map((c) => ({
          type: 'Company' as const,
          id: c.companyId,
        })),
        { type: 'Company', id: 'List' },
        ...handleErrorTags(error),
      ],
    };

    return {
      getCompanies: build.query(getCompaniesQueryOptions),
      getCompaniesWithoutLoading: build.query({
        ...getCompaniesQueryOptions,
        query: (conditions) => ({
          ...getCompaniesQueryFn(conditions),
          headers: internalHeaders({ loading: 'suppress' }),
        }),
      }),
      getCompany: build.query<Company, number>({
        query: (id) => ({
          url: `/companies/${id}`,
        }),
        providesTags: (_res, error, arg) => [
          { type: 'Company', id: arg },
          ...handleErrorTags(error),
        ],
      }),
      getCompanyTypes: build.query<CompanyType[], void>({
        query: () => ({
          url: `/company/types`,
        }),
        providesTags: (_res, error) => handleErrorTags(error),
      }),
      createCompany: build.mutation<void, CompanyFormData>({
        query: (data) => ({
          url: `/companies`,
          method: 'POST',
          body: extinctFalsyFromParams(data),
        }),
        invalidatesTags: [{ type: 'Company', id: 'List' }],
      }),
      editCompany: build.mutation<void, { id: number; data: CompanyFormData }>({
        query: ({ id, data }) => ({
          url: `/companies/${id}`,
          method: 'PUT',
          body: extinctFalsyFromParams(data, ['address3']),
        }),
        invalidatesTags: (_res, _err, args) => [
          { type: 'Company', id: args.id },
          { type: 'Company', id: 'List' },
        ],
      }),
      deleteCompany: build.mutation<void, number>({
        query: (id) => ({
          url: `/companies/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_res, _err, id) => [
          { type: 'Company', id },
          { type: 'Company', id: 'List' },
        ],
      }),
      postRegisterCsvByCompany: build.mutation<void, File>({
        query: (file) => {
          const body = new FormData();
          body.append('csv', file);
          body.append('type', 'company');
          return {
            url: `/register-csv`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: [
          {
            type: 'Company',
            id: 'List',
          },
        ],
      }),
    };
  },
});

export const {
  useGetCompaniesQuery,
  useGetCompaniesWithoutLoadingQuery,
  useGetCompanyQuery,
  useGetCompanyTypesQuery,
  useCreateCompanyMutation,
  useEditCompanyMutation,
  useDeleteCompanyMutation,
  usePostRegisterCsvByCompanyMutation,
} = companyApi;
